
import React from 'react';
import { Card } from "@/components/ui/card";
import { TrendingUp, Calendar } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import type { PerformanceData } from './types';

interface PredictiveAnalysisChartProps {
  performanceData: PerformanceData[];
}

export const PredictiveAnalysisChart: React.FC<PredictiveAnalysisChartProps> = ({ performanceData }) => {
  const [predictedData, targetDate, daysToTarget] = React.useMemo(() => {
    if (performanceData.length < 2) {
      return [[], null, null];
    }

    // Convert performance data to daily averages
    const dailyScores = performanceData.reduce((acc, curr) => {
      const date = new Date(curr.created_at).toLocaleDateString();
      if (!acc[date]) {
        acc[date] = { total: 0, count: 0 };
      }
      acc[date].total += curr.score || 0;
      acc[date].count += 1;
      return acc;
    }, {} as Record<string, { total: number; count: number }>);

    // Create data points for regression
    const points = Object.entries(dailyScores).map(([date, data], index) => ({
      day: index,
      accuracy: data.total / data.count,
      date
    })).filter(point => !isNaN(point.accuracy));

    if (points.length < 2) {
      return [points, null, null];
    }

    // Simple linear regression
    const n = points.length;
    const sumX = points.reduce((acc, p) => acc + p.day, 0);
    const sumY = points.reduce((acc, p) => acc + p.accuracy, 0);
    const sumXY = points.reduce((acc, p) => acc + (p.day * p.accuracy), 0);
    const sumXX = points.reduce((acc, p) => acc + (p.day * p.day), 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    // Generate prediction data (next 30 days)
    const lastDay = points[points.length - 1].day;
    const predictedPoints = Array.from({ length: 30 }, (_, i) => {
      const day = lastDay + i + 1;
      const predictedAccuracy = Math.min(100, Math.max(0, slope * day + intercept));
      return {
        day,
        predicted: predictedAccuracy,
        date: new Date(Date.now() + (i + 1) * 24 * 60 * 60 * 1000).toLocaleDateString()
      };
    });

    // Calculate days until 90% mastery
    const daysTo90 = slope !== 0 ? Math.ceil((90 - intercept) / slope) - lastDay : null;
    const targetDate = daysTo90 && daysTo90 > 0 && daysTo90 < 365
      ? new Date(Date.now() + daysTo90 * 24 * 60 * 60 * 1000).toLocaleDateString()
      : null;

    const combinedData = [
      ...points.map(p => ({
        ...p,
        predicted: null
      })),
      ...predictedPoints
    ];

    return [
      combinedData,
      targetDate,
      daysTo90 && daysTo90 > 0 && daysTo90 < 365 ? daysTo90 : null
    ];
  }, [performanceData]);

  if (!predictedData.length) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-purple-500" />
          Predictive Performance Analysis
        </h2>
        <p className="text-gray-500">Not enough data for prediction yet. Complete more quizzes to see your learning forecast.</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-6">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-purple-500" />
          Predictive Performance Analysis
        </h2>
        {targetDate && daysToTarget && (
          <div className="flex items-center gap-2 bg-purple-100 px-3 py-1 rounded-full">
            <Calendar className="w-4 h-4 text-purple-500" />
            <span className="text-sm text-purple-700">
              Estimated 90% mastery: {targetDate} ({daysToTarget} days)
            </span>
          </div>
        )}
      </div>
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={predictedData}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis 
              dataKey="date" 
              interval="preserveStartEnd"
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis 
              domain={[0, 100]}
              label={{ value: 'Accuracy (%)', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip
              contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc' }}
              formatter={(value: number) => [`${value?.toFixed(1) || 'N/A'}%`, 'Accuracy']}
            />
            <ReferenceLine y={90} stroke="#10B981" strokeDasharray="3 3" label="Mastery Goal (90%)" />
            <Line
              type="monotone"
              dataKey="accuracy"
              stroke="#8B5CF6"
              strokeWidth={2}
              dot={{ fill: '#8B5CF6' }}
              name="Actual Performance"
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="predicted"
              stroke="#C084FC"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              name="Predicted Performance"
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 text-sm text-gray-500">
        <p>• Solid purple line: Actual performance</p>
        <p>• Dashed purple line: Predicted future performance</p>
        <p>• Green line: Mastery goal (90% accuracy)</p>
      </div>
    </Card>
  );
};
